'use client'

import {
  Enterprise as Building2,
  Group as Users,
  Wallet,
  Calculator,
  Education as GraduationCap,
  Building as School,
  CheckmarkFilled as CheckCircle2,
  ArrowLeft,
  ArrowRight,
} from '@carbon/icons-react'
import { useEffect, useRef, useState } from 'react'
import { AnimatedNumber } from '@/components/ui/animated-number'
import Image from 'next/image'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { Button, buttonVariants } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import { PanelDots } from '@/components/fx/panel-dots'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel'

/* ─── Solutions data ─────────────────────────────────────────────────────
   Copy moved verbatim from hero-modern.tsx — not one string reworded.
   Three presentation fields were added per entry for this card design:
     `href`  — where the card links,
     `panel` — the card's solid panel colour (deep tones from the site's
               cool mint/sky family; white text clears 7:1 on all of them),
     `stat`  — the existing `kpi` string split into a big value + label*/
const solutions = [
  {
    num: '01',
    id: 'udc',
    title: 'UDC',
    full: 'Unified Digital Campus',
    accent: '#37ecb0',
    rgb: '55,236,176',
    icon: <Building2 size={22} />,
    image: '/gradients/02.webp',
    tagline: 'The operating system for modern enterprises',
    desc: 'End-to-end enterprise management operations, analytics, finance, and compliance unified in one powerful platform for seamless business execution.',
    features: [
      'Operations Hub',
      'Analytics Suite',
      'Finance Control',
      'Compliance Reports',
    ],
    kpi: '100+ Enterprises',
    href: '/products/udc',
    panel: '#065f46',
    stat: { value: '100+', label: 'Enterprises' },
  },
  {
    num: '02',
    id: 'hrms',
    title: 'HRMS',
    full: 'Human Resource Management',
    accent: '#0ea5e9',
    rgb: '14,165,233',
    icon: <Users size={22} />,
    image: '/gradients/03.webp',
    tagline: 'People-first HR, powered by automation',
    desc: 'Streamline every HR process from hiring and onboarding to attendance, appraisals, and exits with zero-error automation for organizations of any size.',
    features: [
      'Recruitment Suite',
      'Attendance Tracking',
      'Performance Reviews',
      'Employee Portal',
    ],
    kpi: '5K+ Employees Managed',
    href: '/products/hrms',
    panel: '#9f1239',
    stat: { value: '5K+', label: 'Employees Managed' },
  },
  {
    num: '03',
    id: 'payroll',
    title: 'Payroll',
    full: 'Payroll & Compensation Engine',
    accent: '#37ecb0',
    rgb: '55,236,176',
    icon: <Wallet size={22} />,
    image: '/gradients/06.webp',
    tagline: 'Zero-error salaries. Zero compliance risk',
    desc: 'Process salaries in minutes with automated tax, PF/ESI, and TDS calculations. Full statutory compliance built-in for hassle-free payroll management.',
    features: [
      'Auto Tax & TDS',
      'Statutory Compliance',
      'Bulk Disbursal',
      'Digital Payslips',
    ],
    kpi: '₹8.2 Cr / Month',
    href: '/products/payroll',
    panel: '#115e59',
    stat: { value: '₹8.2 Cr', label: 'Per Month' },
  },
  {
    num: '04',
    id: 'accounts',
    title: 'Accounts',
    full: 'Financial Accounting Suite',
    accent: '#0ea5e9',
    rgb: '14,165,233',
    icon: <Calculator size={22} />,
    image: '/gradients/01.webp',
    tagline: 'Real-time financial clarity at every level',
    desc: 'Double-entry ledger, GST-ready invoicing, budget tracking, and audit-proof reports complete financial visibility for businesses and institutions.',
    features: [
      'Double-Entry Ledger',
      'GST Invoicing',
      'Budget Tracking',
      'Audit Trail',
    ],
    kpi: '99.9% Accuracy',
    href: '/products/accounts',
    panel: '#1e40af',
    stat: { value: '99.9%', label: 'Accuracy' },
  },
  {
    num: '05',
    id: 'lms',
    title: 'LMS',
    full: 'Learning Management System',
    accent: '#37ecb0',
    rgb: '55,236,176',
    icon: <GraduationCap size={22} />,
    image: '/gradients/04.webp',
    tagline: 'Learning at scale for teams and institutions',
    desc: 'Deliver blended learning live classes, recorded content, smart assessments, certifications, and progress analytics for corporate training and education.',
    features: [
      'Live & Recorded',
      'Smart Assessments',
      'Certification Engine',
      'Progress Analytics',
    ],
    kpi: '50K+ Learners',
    href: '/products/lms',
    panel: '#9a3412',
    stat: { value: '50K+', label: 'Learners' },
  },
  {
    num: '06',
    id: 'schoolerp',
    title: 'School ERP',
    full: 'School Management System',
    accent: '#0ea5e9',
    rgb: '14,165,233',
    icon: <School size={22} />,
    image: '/gradients/05.webp',
    tagline: 'Everything your school needs in one connected platform',
    desc: 'Simplify admissions, attendance, academics, fees, examinations, communication, and staff management with a centralized system built for schools.',
    features: [
      'Smart Academics',
      'Parent Connect',
      'School Operations',
      'Administrative Control',
    ],
    kpi: 'All-in-One ERP',
    href: '/schoolerp',
    panel: '#6b21a8',
    stat: { value: 'All-in-One', label: 'School ERP' },
  },
]

/* ─── Solutions carousel ────────────────────
   One full-width card per view on an Embla track with a visible overflow,
   so the neighbouring cards peek in from the margins at reduced opacity —
   the hero <section>'s overflow-hidden does the actual clipping at the
   viewport edge. Each card: solid deep-colour panel on the left (copy,
   stat, features, CTA) and the product image filling the right half. ── */
const SolutionsCarousel = () => {
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(true)
  const [headerVisible, setHeaderVisible] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ob = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) setHeaderVisible(true)
      },
      { threshold: 0.1 }
    )
    if (headerRef.current) ob.observe(headerRef.current)
    return () => ob.disconnect()
  }, [])

  useEffect(() => {
    if (!api) return
    const onSelect = () => {
      setCurrent(api.selectedScrollSnap())
      setCanPrev(api.canScrollPrev())
      setCanNext(api.canScrollNext())
    }
    onSelect()
    api.on('select', onSelect)
    api.on('reInit', onSelect)
    return () => {
      api.off('select', onSelect)
      api.off('reInit', onSelect)
    }
  }, [api])

  const goPrev = () => api?.scrollPrev()
  const goNext = () => api?.scrollNext()

  return (
    <div className="relative w-full mt-6 sm:mt-8">
      <div className="container mx-auto relative" style={{ zIndex: 1 }}>
        {/* ── Section header ── */}
        <div
          ref={headerRef}
          className="pt-6 sm:pt-20 pb-8 sm:pb-10"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'none' : 'translateY(16px)',
            transition:
              'opacity 500ms cubic-bezier(0.22,1,0.36,1), transform 500ms cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2
              className="font-heading font-medium tracking-tight text-foreground"
              style={{
                fontSize: 'clamp(22px,2.8vw,36px)',
                letterSpacing: '-0.02em',
                lineHeight: 1.22,
                maxWidth: 690,
              }}
            >
              <span className="block">
                <ChromaText>Every Enterprise needs to grow.</ChromaText>
              </span>
              <span className="block text-muted-foreground">
                Run your business on a reliable platform that adapts to your
                needs.
              </span>
            </h2>
            <div className="flex shrink-0 items-center gap-2 md:pb-1.5">
              <Button
                size="icon"
                aria-label="Previous solution"
                onClick={goPrev}
                disabled={!canPrev}
              >
                <ArrowLeft className="size-4" />
              </Button>
              <Button
                size="icon"
                aria-label="Next solution"
                onClick={goNext}
                disabled={!canNext}
              >
                <ArrowRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* ── Card deck ── */}
        <div className="pb-12 sm:pb-16">
          <Carousel
            setApi={setApi}
            // Finite deck: no loop (looping visibly re-shuffles slides when
            // the viewport overflow is visible) and no autoplay — the deck
            // moves only when the reader moves it.
            opts={{ align: 'start' }}
            aria-label="Solutions"
            className="active:cursor-grabbing"
          >
            <CarouselContent
              className="-ml-4"
              // The peek effect: slides stay visible past the container edge;
              // the hero section's own overflow-hidden clips at the viewport.
              viewportStyle={{ overflow: 'visible' }}
            >
              {solutions.map((s, i) => {
                const dimmed = i !== current
                return (
                  <CarouselItem
                    key={s.id}
                    className="pl-4 basis-[calc(100%-32px)] lg:basis-full"
                  >
                    <Link
                      href={s.href}
                      className={cn(
                        'group relative flex h-full flex-col overflow-hidden rounded-[6px] outline-none transition-opacity duration-300',
                        'focus-visible:outline-2 focus-visible:outline-offset-2',
                        // Flatter deck: copy spreads wider (see the lg
                        // max-w bumps below), so the card gives back height.
                        // lg keeps a touch more than xl so iPad-landscape
                        // widths never pinch the CTA.
                        'lg:flex-row lg:aspect-[1056/448] xl:aspect-[1056/424]',
                        dimmed && 'opacity-40'
                      )}
                      /* The card's own background is the panel colour: the
                         panel and art render on separate compositor layers,
                         and during Embla's fractional-pixel translates their
                         shared edge can open a sub-pixel gap. Whatever peeks
                         through is now the same colour, so no seam line. */
                      style={{ backgroundColor: s.panel }}
                    >
                      {/* One surface, no seam: the card itself carries the
                          panel colour, the dot matrix runs edge to edge
                          beneath everything, and the artwork is a MASKED
                          LAYER ON TOP of the dots — there is no adjacent-
                          surface boundary left to open up during motion. */}
                      <PanelDots />

                      {/* ── Art: masked overlay emerging over the dots — a
                          bottom tail on mobile, a right spine on desktop. */}
                      <div
                        aria-hidden
                        className={cn(
                          'pointer-events-none absolute inset-x-0 bottom-0 h-[250px]',
                          /* Eased multi-stop ramp (≈smoothstep) — the image
                             has no perceivable start line, it just develops. */
                          '[mask-image:linear-gradient(to_bottom,#0000_0%,#0001_16%,#0003_33%,#0007_50%,#000b_68%,#000_86%)]',
                          'lg:inset-x-auto lg:inset-y-0 lg:right-0 lg:h-auto lg:w-[48%]',
                          'lg:[mask-image:linear-gradient(to_right,#0000_0%,#0001_16%,#0003_33%,#0007_50%,#000b_68%,#000_86%)]'
                        )}
                      >
                        <Image
                          src={s.image}
                          alt=""
                          fill
                          quality={90}
                          sizes="(max-width: 1024px) 100vw, 620px"
                          className="object-cover transition-transform duration-500 ease-out will-change-transform group-hover:scale-[1.03] motion-reduce:transition-none"
                        />
                        {/* Grain pass — the pack's blending note (linear
                            light, 55%) has no CSS equivalent; overlay is the
                            closest mode that keeps mid-grey neutral. */}
                        <div
                          className="absolute inset-0"
                          style={{
                            backgroundImage: 'url(/gradients/grain.webp)',
                            backgroundRepeat: 'repeat',
                            backgroundSize: '512px 512px',
                            mixBlendMode: 'overlay',
                            opacity: 0.55,
                          }}
                        />
                      </div>

                      {/* Dark theme runs a neutral canvas — one veil across
                          the whole card mutes it there, lifts on hover. */}
                      <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 hidden bg-black/25 transition-opacity duration-300 dark:block dark:group-hover:opacity-0"
                      />

                      {/* ── Copy ── */}
                      <div className="relative z-10 flex min-h-[380px] min-w-0 flex-1 flex-col p-6 text-white sm:p-7 lg:min-h-0 lg:w-[68%] lg:flex-none lg:p-9">
                        {/* Wordmark row */}
                        <div className="flex w-full items-center gap-2.5">
                          <span aria-hidden>{s.icon}</span>
                          <span className="text-[15px] font-semibold tracking-wide">
                            {s.full}
                          </span>
                        </div>

                        <h3 className="mt-7 max-w-[30rem] text-[26px]/8 font-medium tracking-[-0.01em] lg:mt-6 lg:max-w-[38rem] lg:text-[32px]/10">
                          <span className="text-white">{s.title}.</span>{' '}
                          <span className="text-white/70">{s.tagline}.</span>
                        </h3>

                        <p className="mt-4 max-w-[46ch] text-[13.5px]/6 text-white/75 lg:max-w-[70ch] lg:text-sm/6">
                          {s.desc}
                        </p>

                        {/* Stat + feature checklist */}
                        <div className="mt-7 flex flex-wrap items-end gap-x-10 gap-y-5 lg:mt-6 lg:gap-x-14">
                          <div className="flex flex-col gap-1.5">
                            <p className="text-[32px]/[38px] font-medium tracking-[-0.02em] lg:text-[40px]/[44px]">
                              <AnimatedNumber value={s.stat.value} />
                            </p>
                            <p className="text-[13px]/5 text-white/80 lg:text-sm/6">
                              {s.stat.label}
                            </p>
                          </div>
                          <ul className="grid grid-cols-2 gap-x-6 gap-y-2.5 lg:gap-x-10">
                            {s.features.map((f) => (
                              <li
                                key={f}
                                className="flex items-center gap-2 text-[12.5px] font-medium text-white/85"
                              >
                                <CheckCircle2 className="size-3.5 shrink-0 opacity-80" />
                                {f}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* CTA — a styled span, since the whole card is the link */}
                        <div className="mt-auto pt-7">
                          <span className={cn(buttonVariants(), 'w-fit')}>
                            Explore {s.title}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </CarouselItem>
                )
              })}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </div>
  )
}

export { SolutionsCarousel }
